import React from "react";
import { Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { getBadges, getIsApplicationComplete } from "../../../services/Product/ProductService/productService";
import AgentPicker from "../../AgentPicker/AgentPicker";
import BadgesList from "../../Badges/BadgesList";
import CaseProgressBar from "../CaseProgressBar/CaseProgressBar";
import caseOverviewBoxStyle from "./caseOverviewBoxStyle";
import Button from "@mui/material/Button";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";
import CaseAdditionalClientsTooltipIcon from "../CaseAdditionalClientsTooltipIcon/CaseAdditionalClientsTooltipIcon";
import { OrganizationHintsModalToggle } from "../../OrganizationHints/OrganizationHintsModalToggle";

import { ProductUnion } from "../../../types/ProductUnion";

type CaseOverviewBoxProps = {
  product: ProductUnion;
  refreshPage: Function;
  hasUpdates: boolean;
};

const CaseOverviewBox = ({ product, refreshPage, hasUpdates }: CaseOverviewBoxProps) => {
  const { backofficeUsers, accountManagers, lawyers } = useBackofficeUser();

  const isApplicationComplete = getIsApplicationComplete(product);
  if (!backofficeUsers) {
    return null;
  }

  const { opposingPartyLegalEntity } = product;

  return (
    <Paper
      sx={[
        caseOverviewBoxStyle.fullWidth,
        product.backofficeCase?.status ? caseOverviewBoxStyle[product.backofficeCase.status] : null,
      ]}
    >
      <Grid container>
        <Grid size={12}>
          <Grid
            container
            sx={{
              alignItems: "center",
              justifyContent: "space-between"
            }}>
            <Grid
              size={{
                xs: 12,
                lg: 7
              }}>
              <Grid container spacing={2} sx={{
                alignItems: "center"
              }}>
                <Grid>
                  <Stack direction={"row"} spacing={1} sx={{
                    alignItems: "center"
                  }}>
                    <Typography variant={"h1"} sx={caseOverviewBoxStyle.label}>
                      {product.backofficeCase.label}
                    </Typography>
                    <CaseAdditionalClientsTooltipIcon product={product} fontSize={"large"} />
                  </Stack>
                  {opposingPartyLegalEntity?.organization.strategicHints ||
                  opposingPartyLegalEntity?.organization.insolvencyStatus ? (
                    <OrganizationHintsModalToggle product={product} />
                  ) : (
                    !!product.opposingParty && <>./. {product.opposingParty}</>
                  )}
                </Grid>
                {hasUpdates && (
                  <Grid>
                    <Button onClick={() => refreshPage()}>Neu laden</Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid
              size={{
                xs: 12,
                lg: 5
              }}>
              <Grid container>
                <Grid
                  size={{
                    xs: 12,
                    lg: 6
                  }}>
                  {(isApplicationComplete || product.backofficeCase.lawyer !== null) && (
                    <AgentPicker
                      label={"Legal"}
                      currentAgent={product.backofficeCase.lawyer}
                      product={product}
                      type={"lawyer"}
                      agentList={lawyers}
                      refreshPage={refreshPage}
                    />
                  )}
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    lg: 6
                  }}>
                  <AgentPicker
                    label={"Operations"}
                    currentAgent={product.backofficeCase?.accountManager}
                    product={product}
                    type={"accountManager"}
                    agentList={accountManagers}
                    refreshPage={refreshPage}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <BadgesList badges={getBadges(product)} />
      {isApplicationComplete && <CaseProgressBar product={product} refreshPage={refreshPage} />}
    </Paper>
  );
};

export default CaseOverviewBox;
